import React, { useState, useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  List,
  Paper,
  ScrollArea,
  Stepper,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBuildingHospital,
  IconBulb,
  IconCheck,
  IconClipboardList,
  IconFileCheck,
  IconRocket,
  IconScale,
  IconShieldCheck,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import swal from "sweetalert";
import ComplianceTab from "../Setting/ComplianceTab";
import {
  getComplianceDashboard,
  getComplianceSettings,
  updateComplianceSettings,
} from "../../../services/compliance";

/**
 * Inline full-page setup wizard. When open, it overlays the active portion
 * of the page (everything to the right of the ~320px sidebar) so the user
 * can configure documents and charting rules in place without losing the
 * sidebar for navigation. Steps 2 and 3 embed the real ComplianceTab UI
 * (documents and charting settings) so users make the configuration
 * changes directly inside the wizard. The final step flips
 * commandCenterScoringEnabled to true.
 */
const CommandCenterSetupWizard = ({ opened, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [activating, setActivating] = useState(false);

  // Lock body scroll while the wizard is open so the underlying dashboard
  // doesn't scroll in the background.
  useEffect(() => {
    if (!opened) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [opened]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await updateComplianceSettings({ commandCenterScoringEnabled: true, commandCenterSetupCompleted: true });
      await getComplianceSettings();
      await getComplianceDashboard();
      onClose();
      setActiveStep(0);
      swal({
        title: "Command Center activated",
        text: "Scoring is now live. You'll start seeing insights on the overview, compliance, operations, and finance tabs.",
        icon: "success",
      });
    } catch (err) {
      swal({
        title: "Couldn't activate Command Center",
        text: "Something went wrong while saving your setting. Please try again.",
        icon: "error",
      });
    }
    setActivating(false);
  };

  const TOTAL_STEPS = 7; // 0 welcome, 1 staff, 2 facility, 3 client, 4 charting, 5 weights, 6 completed
  const nextStep = () => setActiveStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const prevStep = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleClose = () => {
    onClose();
  };

  if (!opened) return null;

  // Tip callout shown above each document step to walk users through the
  // two-step interaction: expand a folder, then toggle on the templates
  // they need.
  const DocumentTip = ({ folderExamples }) => (
    <Flex
      align="flex-start"
      gap={12}
      p={14}
      mb={14}
      style={{
        background: "#FFF8EB",
        border: "1px solid #FFD88A",
        borderRadius: 10,
      }}
    >
      <ThemeIcon size={32} radius={8} variant="filled" style={{ background: "#FFAF4A" }}>
        <IconBulb size={18} color="#171717" />
      </ThemeIcon>
      <Box style={{ flex: 1 }}>
        <Text ff="Inter" fz={13} fw={700} color="#92400E" mb={4}>
          How to set this up
        </Text>
        <Text ff="Inter" fz={13} color="#78350F" lh={1.6}>
          <Text component="span" fw={600}>Step 1:</Text> Click a folder below (for
          example {folderExamples}) to expand it and see the templates inside.
          <br />
          <Text component="span" fw={600}>Step 2:</Text> Toggle on the templates
          that are relevant to your facility. Anything left off won't be tracked
          for compliance.
        </Text>
      </Box>
    </Flex>
  );

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "#F8FAFC",
        zIndex: 2500,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ── */}
      <Flex
        align="center"
        gap={14}
        style={{
          background: "#171717",
          padding: "18px 28px",
          flexShrink: 0,
        }}
      >
        <ThemeIcon
          size={40}
          radius={12}
          variant="filled"
          color="yellow"
          style={{ background: "#FFAF4A" }}
        >
          <IconRocket size={20} color="#171717" />
        </ThemeIcon>
        <Box style={{ flex: 1 }}>
          <Text ff="Inter" fz={18} fw={700} color="#fff" lh={1.2}>
            Set up Command Center
          </Text>
          <Text ff="Inter" fz={12} color="rgba(255,255,255,0.65)" mt={2}>
            Configure documents, charting, and score weights to enable scoring.
          </Text>
        </Box>
        <Box
          onClick={handleClose}
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)")
          }
        >
          <IconX size={16} color="#fff" />
        </Box>
      </Flex>

      {/* ── Stepper bar ── */}
      <Box
        style={{
          padding: "18px 28px 10px",
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0,
        }}
      >
        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          allowNextStepsSelect
          size="sm"
          color="yellow"
          styles={{
            stepIcon: { borderColor: "#E5E7EB" },
            separator: { marginLeft: 4, marginRight: 4 },
            content: { display: "none" },
          }}
        >
          <Stepper.Step label="Welcome" icon={<IconRocket size={16} />} />
          <Stepper.Step label="Staff Documents" icon={<IconUser size={16} />} />
          <Stepper.Step label="Facility Documents" icon={<IconBuildingHospital size={16} />} />
          <Stepper.Step label="Client Documents" icon={<IconUsers size={16} />} />
          <Stepper.Step label="Charting" icon={<IconClipboardList size={16} />} />
          <Stepper.Step label="Score Weights" icon={<IconScale size={16} />} />
          <Stepper.Completed />
        </Stepper>
      </Box>

      {/* ── Body ── */}
      <Box style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <ScrollArea style={{ flex: 1 }} offsetScrollbars>
          <Box p={28} style={{ maxWidth: 1280, margin: "0 auto" }}>
            {activeStep === 0 && (
              <Box mx={-28} mt={-28}>
                {/* Gradient hero */}
                <Box
                  style={{
                    background: "linear-gradient(135deg, #1E293B 0%, #0F172A 60%, #1E1B4B 100%)",
                    padding: "0 0 64px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Decorative glows */}
                  <Box style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,186,69,0.05)" }} />
                  <Box style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(99,102,241,0.05)" }} />
                  <Box style={{ position: "absolute", top: 60, left: "50%", width: 400, height: 400, borderRadius: "50%", background: "rgba(255,186,69,0.02)", transform: "translateX(-50%)" }} />

                  {/* Hero content */}
                  <Flex
                    style={{ padding: "56px 48px 0", position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto" }}
                    align="center"
                    gap={48}
                  >
                    {/* Left: text */}
                    <Box style={{ flex: "0 0 460px" }}>
                      <Badge
                        variant="filled"
                        size="lg"
                        radius="md"
                        mb={20}
                        styles={{
                          root: {
                            background: "rgba(255,186,69,0.15)",
                            color: "#FFBA45",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: 13,
                            padding: "6px 16px",
                          },
                        }}
                      >
                        Command Center setup
                      </Badge>
                      <Text ff="Inter" fw={800} fz={30} c="#FFF" mb={14} lh="38px">
                        Let's get your Command Center ready
                      </Text>
                      <Text ff="Inter" fz={15} c="rgba(255,255,255,0.6)" lh="24px" mb={20}>
                        Command Center scores compliance, operations, and finance in real
                        time. Before it can generate accurate insights, we need to tell it
                        what to look for. This short setup walks you through everything,
                        and changes save automatically as you go.
                      </Text>
                      <Text ff="Inter" fz={13} c="rgba(255,255,255,0.45)" mb={22} lh="22px">
                        Takes about 5 minutes. You can come back and adjust anything from
                        Settings &rarr; Command Center later.
                      </Text>
                      <Flex gap={12}>
                        <Button
                          variant="filled"
                          radius="md"
                          size="md"
                          onClick={nextStep}
                          styles={{
                            root: {
                              background: "linear-gradient(135deg, #FFAF4A, #FF6B35)",
                              fontFamily: "Inter",
                              fontWeight: 600,
                              border: "none",
                              padding: "10px 28px",
                              fontSize: 14,
                            },
                          }}
                          leftIcon={<IconRocket size={16} />}
                        >
                          Start setup
                        </Button>
                        <Button
                          variant="subtle"
                          radius="md"
                          size="md"
                          onClick={handleClose}
                          styles={{
                            root: {
                              fontFamily: "Inter",
                              fontWeight: 500,
                              color: "rgba(255,255,255,0.55)",
                              fontSize: 14,
                              "&:hover": { color: "#FFF", background: "rgba(255,255,255,0.08)" },
                            },
                          }}
                        >
                          Maybe later
                        </Button>
                      </Flex>
                    </Box>

                    {/* Right: illustration (mini dashboard) */}
                    <Box style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                      <svg width="460" height="260" viewBox="0 0 480 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="0" y="20" width="480" height="250" rx="16" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                        {/* Compliance score */}
                        <rect x="20" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" />
                        <circle cx="44" cy="60" r="14" fill="none" stroke="rgba(255,186,69,0.2)" strokeWidth="3" />
                        <circle cx="44" cy="60" r="14" fill="none" stroke="#FFBA45" strokeWidth="3" strokeDasharray="66 88" strokeLinecap="round" transform="rotate(-90 44 60)" />
                        <text x="44" y="64" textAnchor="middle" fill="#FFBA45" fontSize="9" fontWeight="800" fontFamily="Inter">76</text>
                        <text x="82" y="57" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="7.5" fontWeight="600" fontFamily="Inter">Compliance</text>
                        <text x="82" y="68" textAnchor="middle" fill="#FFBA45" fontSize="6.5" fontWeight="500" fontFamily="Inter">Needs Attention</text>
                        {/* Documents */}
                        <rect x="135" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" />
                        <text x="170" y="57" fill="#22C55E" fontSize="13" fontWeight="700" fontFamily="Inter">94%</text>
                        <text x="170" y="68" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="Inter">Complete</text>
                        <rect x="145" y="82" width="84" height="10" rx="5" fill="rgba(255,255,255,0.05)" />
                        <text x="187" y="90" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6" fontWeight="500" fontFamily="Inter">Documents tracked</text>
                        {/* Charting */}
                        <rect x="250" y="36" width="105" height="66" rx="10" fill="rgba(255,255,255,0.08)" />
                        <rect x="264" y="54" width="3" height="12" rx="1.5" fill="#F59E0B" opacity="0.4" />
                        <rect x="269" y="58" width="3" height="8" rx="1.5" fill="#F59E0B" opacity="0.6" />
                        <rect x="274" y="51" width="3" height="15" rx="1.5" fill="#F59E0B" />
                        <rect x="279" y="56" width="3" height="10" rx="1.5" fill="#F59E0B" opacity="0.5" />
                        <text x="300" y="57" fill="#F59E0B" fontSize="13" fontWeight="700" fontFamily="Inter">72%</text>
                        <text x="300" y="68" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="Inter">Charting</text>
                        {/* Revenue */}
                        <rect x="365" y="36" width="95" height="66" rx="10" fill="rgba(255,255,255,0.08)" />
                        <text x="412" y="56" textAnchor="middle" fill="#10B981" fontSize="13" fontWeight="700" fontFamily="Inter">$24.5K</text>
                        <polyline points="378,68 388,66 398,67 408,63 418,64 432,59" stroke="#10B981" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        <text x="412" y="90" textAnchor="middle" fill="#10B981" fontSize="6" fontWeight="600" fontFamily="Inter">Finance</text>
                        {/* Task rows */}
                        <rect x="20" y="112" width="220" height="140" rx="10" fill="rgba(255,255,255,0.05)" />
                        <text x="36" y="132" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="600" fontFamily="Inter">Staff Tasks</text>
                        <circle cx="36" cy="152" r="8" fill="rgba(59,130,246,0.3)" />
                        <rect x="52" y="148" width="120" height="6" rx="3" fill="rgba(255,255,255,0.08)" />
                        <rect x="52" y="148" width="96" height="6" rx="3" fill="#3B82F6" />
                        <text x="184" y="154" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">4/5</text>
                        <circle cx="36" cy="172" r="8" fill="rgba(168,85,247,0.3)" />
                        <rect x="52" y="168" width="120" height="6" rx="3" fill="rgba(255,255,255,0.08)" />
                        <rect x="52" y="168" width="48" height="6" rx="3" fill="#F59E0B" />
                        <text x="184" y="174" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Inter">2/5</text>
                        {/* Facility Health */}
                        <rect x="252" y="112" width="208" height="140" rx="10" fill="rgba(255,255,255,0.05)" />
                        <text x="268" y="132" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="600" fontFamily="Inter">Facility Health</text>
                        <rect x="268" y="144" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                        <rect x="268" y="144" width="120" height="8" rx="4" fill="#22C55E" />
                        <rect x="268" y="174" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                        <rect x="268" y="174" width="85" height="8" rx="4" fill="#F59E0B" />
                        <rect x="268" y="204" width="174" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                        <rect x="268" y="204" width="30" height="8" rx="4" fill="#EF4444" />
                      </svg>
                    </Box>
                  </Flex>
                </Box>

                {/* What you'll configure */}
                <Box style={{ padding: "56px 48px 16px", maxWidth: 1100, margin: "0 auto" }}>
                  <Text ff="Inter" fw={600} fz={13} c="#9CA3AF" tt="uppercase" lts={1} mb={24} ta="center">
                    What you'll configure in this wizard
                  </Text>
                  <Flex gap={16} mb={16} wrap="wrap" justify="center">
                    {[
                      {
                        icon: <IconFileCheck size={22} />,
                        color: "#22C55E",
                        bg: "#F0FDF4",
                        title: "Document templates",
                        desc: "Turn on the files you require for staff, facilities, and clients so the platform knows what to track.",
                      },
                      {
                        icon: <IconClipboardList size={22} />,
                        color: "#F59E0B",
                        bg: "#FFF7ED",
                        title: "Charting rules",
                        desc: "Decide which notes, tasks, and MAR entries count toward compliance and on what schedule.",
                      },
                      {
                        icon: <IconScale size={22} />,
                        color: "#3B82F6",
                        bg: "#EFF6FF",
                        title: "Score weights",
                        desc: "Pick a preset or customize how MAR, tasks, and notes combine into the staff compliance score.",
                      },
                    ].map((card, idx) => (
                      <Paper
                        key={idx}
                        p="lg"
                        radius="md"
                        style={{
                          border: "1px solid #E5E7EB",
                          background: "#FFF",
                          flex: "1 1 0",
                          minWidth: 260,
                        }}
                      >
                        <Flex align="center" gap={14} mb={10}>
                          <Box
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: 10,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: card.bg,
                              color: card.color,
                              flexShrink: 0,
                            }}
                          >
                            {card.icon}
                          </Box>
                          <Text ff="Inter" fw={700} fz={16} c="#171717">
                            {card.title}
                          </Text>
                        </Flex>
                        <Text ff="Inter" fz={13} c="#6B7280" lh="20px">
                          {card.desc}
                        </Text>
                      </Paper>
                    ))}
                  </Flex>
                  <Text ff="Inter" fz={12} color="#9CA3AF" ta="center" mt={14} lh={1.6}>
                    When you finish the last step, scoring turns on and the Overview,
                    Compliance, Operations, and Finance tabs come to life.
                  </Text>
                </Box>
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={4}>
                  Activate staff document templates
                </Text>
                <Text ff="Inter" fz={13} color="#6B7280" mb={16} lh={1.6}>
                  These are the files every staff member needs on record (I-9, CPR,
                  TB test, etc.). Toggle on the templates you require and use "Add
                  Template" or "Add Folder" for anything custom. Changes save
                  automatically.
                </Text>
                <DocumentTip folderExamples="Health Information, History, Training" />
                <Box style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 20px 20px" }}>
                  <ComplianceTab embeddedSubTab="documents" embeddedDocCategory="staff" wizardHighlight />
                </Box>
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={4}>
                  Activate facility document templates
                </Text>
                <Text ff="Inter" fz={13} color="#6B7280" mb={16} lh={1.6}>
                  Licenses, inspections, and insurance records tracked per facility
                  (operating license, fire inspection, liability insurance, etc.).
                  Toggle on the templates you require and add custom ones as needed.
                </Text>
                <DocumentTip folderExamples="Licenses, Inspections, Insurance" />
                <Box style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 20px 20px" }}>
                  <ComplianceTab embeddedSubTab="documents" embeddedDocCategory="facility" wizardHighlight />
                </Box>
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={4}>
                  Activate client document templates
                </Text>
                <Text ff="Inter" fz={13} color="#6B7280" mb={16} lh={1.6}>
                  Intake paperwork, care plans, and consent forms tracked per client
                  (service plan, emergency contact, consent, etc.). Toggle on the
                  templates you require and add custom ones as needed.
                </Text>
                <DocumentTip folderExamples="Intake, Care Plans, Consent Forms" />
                <Box style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 20px 20px" }}>
                  <ComplianceTab embeddedSubTab="documents" embeddedDocCategory="client" wizardHighlight />
                </Box>
              </Box>
            )}

            {activeStep === 4 && (
              <Box>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={4}>
                  Configure charting rules
                </Text>
                <Text ff="Inter" fz={13} color="#6B7280" mb={16} lh={1.6}>
                  Add a charting rule, set its schedule, or toggle default rules on and
                  off. All changes apply immediately to the compliance engine.
                </Text>
                <Flex
                  align="flex-start"
                  gap={12}
                  p={14}
                  mb={14}
                  style={{
                    background: "#FFF8EB",
                    border: "1px solid #FFD88A",
                    borderRadius: 10,
                  }}
                >
                  <ThemeIcon size={32} radius={8} variant="filled" style={{ background: "#FFAF4A" }}>
                    <IconBulb size={18} color="#171717" />
                  </ThemeIcon>
                  <Box style={{ flex: 1 }}>
                    <Text ff="Inter" fz={13} fw={700} color="#92400E" mb={4}>
                      How to set this up
                    </Text>
                    <Text ff="Inter" fz={13} color="#78350F" lh={1.6}>
                      <Text component="span" fw={600}>Step 1:</Text> Start with the
                      highlighted <Text component="span" fw={600}>Note</Text> card — it
                      controls whether progress notes count toward compliance.
                      <br />
                      <Text component="span" fw={600}>Step 2:</Text> Use the Active
                      toggle in the top right of the card to turn the category on or off.
                      Repeat for Task and MAR, and click <Text component="span" fw={600}>Add Charting Rule</Text> if you need extra rules beyond the defaults.
                    </Text>
                  </Box>
                </Flex>
                <Box style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 20px 20px" }}>
                  <ComplianceTab embeddedSubTab="charting" wizardHighlight />
                </Box>
              </Box>
            )}

            {activeStep === 5 && (
              <Box>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={4}>
                  Configure compliance score weights
                </Text>
                <Text ff="Inter" fz={13} color="#6B7280" mb={16} lh={1.6}>
                  Pick a service-type preset or set a custom split. Weights must sum
                  to 100 and control how MAR, tasks, and notes blend into the overall
                  staff compliance score.
                </Text>
                <Box style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "4px 20px 20px" }}>
                  <ComplianceTab embeddedSubTab="settings" wizardHighlight />
                </Box>
              </Box>
            )}

            {activeStep === 6 && (
              <Box py={40} style={{ textAlign: "center" }}>
                <ThemeIcon size={72} radius="xl" color="yellow" variant="light" mx="auto" mb={18}>
                  <IconShieldCheck size={36} color="#FFAF4A" />
                </ThemeIcon>
                <Text ff="Inter" fz={22} fw={700} color="#171717" mb={10}>
                  Ready to activate
                </Text>
                <Text ff="Inter" fz={14} color="#4B5563" mb={24} lh={1.6} maw={520} mx="auto">
                  Once you activate, scoring starts running against your configuration
                  and the Overview, Compliance, Operations, and Finance tabs light up.
                  You can disable it any time from Settings &rarr; Command Center &rarr;
                  Settings.
                </Text>
                <Button
                  size="md"
                  radius={10}
                  ff="Inter"
                  fw={700}
                  loading={activating}
                  onClick={handleActivate}
                  styles={{
                    root: {
                      background: "linear-gradient(135deg, #FFDA7A, #FFBA45)",
                      color: "#0F172A",
                      padding: "12px 32px",
                      boxShadow: "0 4px 14px rgba(255,186,69,0.3)",
                      "&:hover": { background: "linear-gradient(135deg, #FFBA45, #FFAF4A)" },
                    },
                  }}
                  leftIcon={<IconRocket size={18} />}
                >
                  Activate Command Center
                </Button>
              </Box>
            )}
          </Box>
        </ScrollArea>

        {/* ── Footer nav ── */}
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: "14px 28px",
            background: "#fff",
            borderTop: "1px solid #E5E7EB",
            flexShrink: 0,
          }}
        >
          <Button
            variant="subtle"
            color="gray"
            radius={10}
            ff="Inter"
            onClick={prevStep}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          <Text ff="Inter" fz={12} color="#9CA3AF">
            Step {Math.min(activeStep + 1, TOTAL_STEPS)} of {TOTAL_STEPS}
          </Text>
          {activeStep < TOTAL_STEPS - 1 ? (
            <Button
              radius={10}
              ff="Inter"
              fw={600}
              onClick={nextStep}
              styles={{
                root: {
                  background: "#171717",
                  color: "#fff",
                  "&:hover": { background: "#000" },
                },
              }}
            >
              {activeStep === 0 ? "Get started" : "Next"}
            </Button>
          ) : (
            <Box style={{ width: 72 }} />
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default CommandCenterSetupWizard;
